import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

interface CameraModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImageSelected: (base64Image: string, mimeType: string) => void;
}

const CameraModal: React.FC<CameraModalProps> = ({ isOpen, onClose, onImageSelected }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [capturedImage, setCapturedImage] = useState<string | null>(null);
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
    const [error, setError] = useState<string | null>(null);
    const [permissionState, setPermissionState] = useState<'prompt' | 'granted' | 'denied' | 'checking'>('checking');
    
    // Check if truly native (not just a PWA in a browser)
    const isNative = Capacitor.isNativePlatform();

    // WEB: Start HTML5 Camera
    const startWebCamera = useCallback(async (mode: 'user' | 'environment') => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
        }
        try {
            const newStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: mode,
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            });
            if (videoRef.current) {
                videoRef.current.srcObject = newStream;
            }
            setStream(newStream);
            setError(null);
        } catch (err) {
            console.error("Error accessing camera:", err);
            // Provide specific error messages for web users
            if (String(err).includes("NotAllowedError") || String(err).includes("PermissionDenied")) {
                setPermissionState('denied');
            } else {
                setError("Could not access camera. Please check your permissions.");
            }
        }
    }, [stream]);

    // NATIVE: Trigger Capacitor Camera
    const launchNativeCamera = useCallback(async () => {
        try {
            const image = await Camera.getPhoto({
                quality: 80,
                width: 1024,
                allowEditing: false, // Disabling native edit prevents some iOS crashes; we have our own editor anyway
                resultType: CameraResultType.DataUrl,
                source: CameraSource.Camera
            });

            if (image.dataUrl) {
                // Return image immediately and close
                onImageSelected(image.dataUrl, `image/${image.format}`);
                onClose();
            } else {
                onClose();
            }
        } catch (e) {
            console.log("Camera dismissed or error:", e);
            // If native camera fails unexpectedly, fallback or close
            onClose();
        }
    }, [onImageSelected, onClose]);

    // Check permission status on mount / open
    useEffect(() => {
        if (!isOpen) {
            setPermissionState('checking');
            return;
        }

        const checkPermission = async () => {
            if (isNative) {
                try {
                    const status = await Camera.checkPermissions();
                    if (status.camera === 'granted') {
                        setPermissionState('granted');
                        launchNativeCamera();
                    } else if (status.camera === 'denied') {
                        setPermissionState('denied');
                    } else {
                        setPermissionState('prompt');
                    }
                } catch (e) {
                    setPermissionState('prompt');
                }
            } else {
                if (navigator.permissions && navigator.permissions.query) {
                    try {
                        const status = await navigator.permissions.query({ name: 'camera' as PermissionName });
                        if (status.state === 'granted') {
                            setPermissionState('granted');
                            startWebCamera(facingMode);
                        } else if (status.state === 'denied') {
                            setPermissionState('denied');
                        } else {
                            setPermissionState('prompt');
                        }
                    } catch (e) {
                        // Fallback: request permission directly by letting user trigger
                        setPermissionState('prompt');
                    }
                } else {
                    setPermissionState('prompt');
                }
            }
        };

        checkPermission();
    }, [isOpen, isNative]);

    // Request Access manually via custom-themed CTA
    const handleRequestAccess = async () => {
        if (isNative) {
            try {
                const requestResult = await Camera.requestPermissions();
                if (requestResult.camera === 'granted') {
                    setPermissionState('granted');
                    launchNativeCamera();
                } else {
                    setPermissionState('denied');
                }
            } catch (err) {
                console.error("Native request error:", err);
                setPermissionState('denied');
            }
        } else {
            try {
                // Trigger actual browser prompt
                const newStream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: facingMode,
                        width: { ideal: 1280 },
                        height: { ideal: 720 }
                    }
                });
                if (videoRef.current) {
                    videoRef.current.srcObject = newStream;
                }
                setStream(newStream);
                setPermissionState('granted');
                setError(null);
            } catch (err) {
                console.error("Web request error:", err);
                setPermissionState('denied');
            }
        }
    };

    useEffect(() => {
        if (!isOpen) {
            // Cleanup web stream if closed
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
                setStream(null);
            }
            setCapturedImage(null);
            setError(null);
        }
        
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [isOpen, stream]);

    // WEB: Switch Camera
    const handleSwitchCamera = () => {
        const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
        setFacingMode(newFacingMode);
        startWebCamera(newFacingMode);
    };

    // WEB: Capture Frame
    const handleCapture = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const context = canvas.getContext('2d');
            if (context) {
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL('image/jpeg');
                setCapturedImage(dataUrl);
                if (stream) {
                    stream.getTracks().forEach(track => track.stop());
                }
            }
        }
    };

    const handleRetake = () => {
        setCapturedImage(null);
        startWebCamera(facingMode);
    };

    const handleUsePhoto = () => {
        if (capturedImage) {
            onImageSelected(capturedImage, 'image/jpeg');
            onClose();
        }
    };
    
    if (!isOpen) return null;

    // NATIVE UI - Granted: Just a black background while native camera plugin runs
    if (isNative && permissionState === 'granted') {
        return <div className="fixed inset-0 bg-black z-[70]" />;
    }

    // THEMED PRE-FLIGHT / PRE-PERMISSION POPUP
    if (permissionState === 'prompt' || permissionState === 'checking') {
        return (
            <div className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
                <div 
                    className="bg-gradient-to-br from-[#1a0537] to-[#3b0764] rounded-2xl shadow-2xl w-full max-w-sm flex flex-col animate-modal-pop-in origin-center text-white border border-purple-500/30 overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="relative p-6 text-center flex flex-col items-center">
                        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white" aria-label="Close">
                            <i className="ph-duotone ph-x text-2xl"></i>
                        </button>
                        
                        <div className="w-20 h-20 rounded-full bg-purple-500/10 border border-purple-400/20 flex items-center justify-center mb-6 mt-4">
                            <i className="ph-duotone ph-camera text-4xl text-[var(--accent-pink)] animate-pulse"></i>
                        </div>

                        <h2 className="text-2xl font-orbitron font-bold uppercase tracking-wider text-yellow-300 mb-2">
                            Enable Camera
                        </h2>
                        
                        <p className="text-gray-300 text-sm leading-relaxed mb-6 px-2">
                            Snap live pictures to create custom social guessing puzzles and clues for your friends!
                        </p>
                        
                        <button 
                            onClick={handleRequestAccess}
                            className="w-full bg-gradient-to-r from-[var(--accent-pink)] to-purple-600 hover:opacity-90 active:scale-95 text-white font-orbitron font-bold py-3 px-6 rounded-xl text-md uppercase tracking-wider transition-all shadow-[0_4px_15px_rgba(236,72,153,0.3)] mb-3"
                        >
                            Allow Access
                        </button>
                        
                        <button 
                            onClick={onClose}
                            className="w-full bg-white/5 hover:bg-white/10 active:scale-95 text-gray-300 font-bold py-3 px-6 rounded-xl text-sm transition-all border border-white/10"
                        >
                            Not Now
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // THEMED DENIED INSTRUCTION CARD
    if (permissionState === 'denied') {
        return (
            <div className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
                <div 
                    className="bg-gradient-to-br from-[#1a0537] to-[#3b0764] rounded-2xl shadow-2xl w-full max-w-sm flex flex-col animate-modal-pop-in origin-center text-white border border-purple-500/30 overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="relative p-6 text-center flex flex-col items-center">
                        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white" aria-label="Close">
                            <i className="ph-duotone ph-x text-2xl"></i>
                        </button>
                        
                        <div className="w-20 h-20 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6 mt-4">
                            <i className="ph-duotone ph-warning-circle text-4xl text-red-400 animate-bounce"></i>
                        </div>

                        <h2 className="text-xl font-orbitron font-bold uppercase tracking-wider text-red-400 mb-2">
                            Permission Blocked
                        </h2>
                        
                        <p className="text-gray-300 text-sm leading-relaxed mb-6 px-3">
                            Camera access has been disabled. To create picture clues, please open your device settings, locate <span className="font-bold text-white">What Was It?</span>, and toggle camera permissions to ON.
                        </p>
                        
                        <button 
                            onClick={onClose}
                            className="w-full bg-gradient-to-r from-[var(--accent-pink)] to-purple-600 hover:opacity-90 active:scale-95 text-white font-orbitron font-bold py-3 px-6 rounded-xl text-md uppercase tracking-wider transition-all shadow-[0_4px_15px_rgba(236,72,153,0.3)]"
                        >
                            Got It
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // WEB CAMERA VIEW
    return (
        <div className="fixed inset-0 bg-black z-[70] flex flex-col items-center justify-center animate-fade-in" onClick={onClose}>
            <div className="relative w-full h-[100dvh] max-h-screen overflow-hidden bg-black" onClick={e => e.stopPropagation()}>
                
                {error && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white p-4 z-50">
                        <i className="ph-duotone ph-warning-circle text-5xl text-red-500 mb-4"></i>
                        <h2 className="text-xl font-bold mb-2">Camera Error</h2>
                        <p className="text-center">{error}</p>
                        <button onClick={onClose} className="mt-6 bg-[var(--primary-dark-purple)] font-bold py-2 px-4 rounded-lg">Close</button>
                    </div>
                )}

                {!error && capturedImage ? (
                    // Confirmation View (Web Only)
                    <>
                        <img 
                            src={capturedImage} 
                            alt="Captured preview" 
                            className="absolute inset-0 w-full h-full object-contain bg-black" 
                        />
                        <div 
                            className="absolute bottom-0 left-0 right-0 flex justify-around items-center p-4 bg-gradient-to-t from-black/90 via-black/50 to-transparent z-20"
                            style={{ paddingBottom: 'max(8rem, env(safe-area-inset-bottom))' }}
                        >
                            <button onClick={handleRetake} className="text-white font-bold text-lg px-6 py-2 rounded-full bg-black/30 backdrop-blur-md">Retake</button>
                            <button onClick={handleUsePhoto} className="bg-[var(--accent-pink)] text-white font-bold py-3 px-8 rounded-full text-lg shadow-lg">Use Photo</button>
                        </div>
                    </>
                ) : (
                    // Live Camera View (Web Only)
                    <>
                        <video 
                            ref={videoRef} 
                            autoPlay 
                            playsInline 
                            className="absolute inset-0 w-full h-full object-cover bg-black" 
                        />
                        <div className="absolute top-4 left-4 z-20">
                            <button onClick={onClose} className="bg-black/40 backdrop-blur-sm text-white rounded-full h-10 w-10 flex items-center justify-center text-2xl shadow-md">
                                <i className="ph-duotone ph-x"></i>
                            </button>
                        </div>
                        <div 
                            className="absolute bottom-0 left-0 right-0 flex justify-around items-center p-4 bg-gradient-to-t from-black/90 via-black/50 to-transparent z-20"
                            style={{ paddingBottom: 'max(8rem, env(safe-area-inset-bottom))' }}
                        >
                            <button onClick={handleSwitchCamera} className="bg-black/40 backdrop-blur-sm text-white rounded-full h-12 w-12 flex items-center justify-center text-3xl shadow-lg">
                                <i className="ph-duotone ph-camera-rotate"></i>
                            </button>
                            <button onClick={handleCapture} className="w-20 h-20 rounded-full bg-white border-4 border-gray-300 shadow-xl active:scale-95 transition-transform"></button>
                            <div className="w-12 h-12"></div>
                        </div>
                        <canvas ref={canvasRef} className="hidden"></canvas>
                    </>
                )}
            </div>
        </div>
    );
};

export default CameraModal;